/**
 * MoJ Q&A Wave 3 — court / contract / corporate deep dives.
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-06)
 * - นักแปลที่ขึ้นทะเบียนกับกระทรวงยุติธรรมรับรองความถูกต้องของคำแปล
 * - นิติกรณ์: กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ
 *   200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th
 * - ไทยยังไม่ออกตรา Apostille จนกว่าอนุสัญญาจะมีผลกับไทย 28 ก.พ. 2027
 * - ห้ามระบุค่าบริการของเรา · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */
import type { MojQA } from "./questions-500";

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — ระเบียบของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันรูปแบบเอกสารกับผู้รับเอกสารก่อนยื่นทุกครั้ง";
const CTA =
  "สอบถามค่าบริการและรายการเอกสารเฉพาะเคสได้ทางโทรศัพท์ LINE หรืออีเมล";
const FEE =
  "ค่าธรรมเนียมนิติกรณ์เป็นอัตราของกรมการกงสุล 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน";

export const MOJ_QA_WAVE3: MojQA[] = [
  {
    slug: "moj-w3-คำพิพากษาใช้ต่างประเทศ",
    q: "คำพิพากษาศาลไทยจะนำไปใช้ในต่างประเทศ ต้องแปลและรับรองแบบไหน",
    aShort:
      "ต้องคัดสำเนาคำพิพากษาพร้อมคำรับรองจากศาลที่ออก แล้วแปลโดยนักแปลที่รับรองความถูกต้องของคำแปล จากนั้นยื่นนิติกรณ์กรมการกงสุล และประทับตราสถานทูตปลายทางเมื่อปลายทางยังกำหนด",
    aLong:
      "จุดที่ถูกตีกลับบ่อยคือใช้สำเนาถ่ายเองแทนสำเนาที่ศาลรับรอง และแปลไม่ครบส่วนท้ายที่มีลายมือชื่อผู้พิพากษาและตราศาล คำแปลต้องคงเลขคดี วันที่ และชื่อคู่ความให้ตรงตัวสะกดในหนังสือเดินทาง · " +
      FEE + " · " + CTA + " · " + TAIL,
    synonyms: [
      "แปลคำพิพากษาไปต่างประเทศ",
      "certified translation thai court judgment",
      "รับรองคำพิพากษา กงสุล",
    ],
    cluster: "court",
  },
  {
    slug: "moj-w3-คำสั่งศาลหย่า",
    q: "คำสั่งศาลเรื่องหย่าและอำนาจปกครองบุตร ต้องเตรียมอะไรเพิ่ม",
    aShort:
      "ต้องใช้คำสั่งหรือคำพิพากษาที่ศาลรับรอง พร้อมหนังสือรับรองคดีถึงที่สุดเมื่อปลายทางกำหนด แล้วแปลและเข้าสายนิติกรณ์ตามลำดับ",
    aLong:
      "ทะเบียนราษฎรต่างประเทศส่วนใหญ่ต้องการหลักฐานว่าคดีถึงที่สุดแล้ว ไม่ใช่เพียงคำสั่งชั้นต้น หากมีข้อกำหนดเรื่องอำนาจปกครองบุตร ให้แปลข้อกำหนดนั้นครบทุกวรรค เพราะการตัดทอนทำให้เอกสารใช้ไม่ได้ · " +
      CTA + " · " + TAIL,
    synonyms: ["แปลคำสั่งหย่า", "divorce decree translation thailand", "อำนาจปกครองบุตร แปลรับรอง"],
    cluster: "court",
  },
  {
    slug: "moj-w3-สัญญาสองภาษา",
    q: "สัญญาสองภาษาควรกำหนดภาษาใดให้มีผลบังคับ",
    aShort:
      "ควรระบุข้อกำหนดภาษาที่ใช้บังคับ (governing language) ให้ชัดเจนหนึ่งภาษา และให้คำแปลอีกภาษาเป็นฉบับอ้างอิง เพื่อลดข้อพิพาทเรื่องการตีความ",
    aLong:
      "หากสัญญาต้องยื่นต่อหน่วยงานไทย คำแปลภาษาไทยที่รับรองความถูกต้องมักจำเป็น แม้ภาษาบังคับเป็นภาษาอังกฤษ ควรจัดรูปเล่มแบบคอลัมน์คู่หรือแนบคำแปลท้ายเล่ม พร้อมเลขข้อที่ตรงกันทุกข้อเพื่อให้ตรวจทานได้ · " +
      CTA + " · " + TAIL,
    synonyms: ["สัญญาสองภาษา ภาษาบังคับ", "bilingual contract governing language thailand", "แปลสัญญายื่นราชการ"],
    cluster: "contract",
  },
  {
    slug: "moj-w3-แปลสัญญาจ้างงานต่างชาติ",
    q: "สัญญาจ้างงานสำหรับพนักงานต่างชาติต้องแปลเป็นภาษาไทยไหม",
    aShort:
      "เมื่อยื่นประกอบใบอนุญาตทำงานหรือใช้กับหน่วยงานไทย โดยทั่วไปต้องมีคำแปลภาษาไทยที่รับรองความถูกต้องแนบพร้อมฉบับภาษาต้นทาง",
    aLong:
      "รายละเอียดที่เจ้าหน้าที่ตรวจละเอียดคือ ตำแหน่งงาน ขอบเขตหน้าที่ ระยะเวลาจ้าง และชื่อนายจ้างตามหนังสือรับรองบริษัท ตัวสะกดชื่อบริษัทในคำแปลต้องตรงกับหนังสือรับรองทุกตัวอักษร · " +
      CTA + " · " + TAIL,
    synonyms: ["แปลสัญญาจ้าง work permit", "employment contract thai translation", "สัญญาจ้างต่างชาติ แปลไทย"],
    cluster: "contract",
  },
  {
    slug: "moj-w3-เอกสารบริษัทไปต่างประเทศ",
    q: "หนังสือรับรองบริษัทและข้อบังคับ ใช้ต่างประเทศต้องผ่านกี่ชั้น",
    aShort:
      "ต้องคัดหนังสือรับรองใหม่จากกรมพัฒนาธุรกิจการค้าให้อยู่ในอายุที่ปลายทางรับ แล้วแปล ยื่นนิติกรณ์กรมการกงสุล และประทับตราสถานทูตปลายทางถ้ากำหนด",
    aLong:
      "ปลายทางหลายแห่งรับเอกสารที่คัดใหม่ภายในสามถึงหกเดือน ควรตรวจอายุเอกสารก่อนเริ่มแปลเพื่อไม่ต้องทำซ้ำ กรณีเอกสารมีหลายฉบับ ให้ยื่นชุดเดียวกันพร้อมกันเพื่อให้วันที่ตราประทับสอดคล้อง · " +
      FEE + " · " + CTA + " · " + TAIL,
    synonyms: [
      "แปลหนังสือรับรองบริษัท ไปต่างประเทศ",
      "company affidavit legalisation thailand",
      "รับรองข้อบังคับบริษัท กงสุล",
    ],
    cluster: "corporate",
  },
  {
    slug: "moj-w3-งบการเงินยื่นต่างประเทศ",
    q: "งบการเงินและ ภ.พ.20 ต้องแปลรับรองแบบไหนเมื่อยื่นคู่ค้าต่างประเทศ",
    aShort:
      "ใช้สำเนาที่หน่วยงานผู้ออกหรือผู้สอบบัญชีรับรอง แปลโดยผู้รับรองความถูกต้องของคำแปล แล้วเข้าสายนิติกรณ์ตามที่ปลายทางกำหนด",
    aLong:
      "ตัวเลขในคำแปลต้องตรงทุกหลักและคงหน่วยสกุลเงินเดิม ไม่แปลงค่า หากปลายทางเป็นธนาคารหรือหน่วยงานประมูล มักขอเอกสารชุดล่าสุดและตราประทับที่ออกไม่เกินหกเดือน · " +
      CTA + " · " + TAIL,
    synonyms: ["แปลงบการเงิน ยื่นต่างประเทศ", "financial statement certified translation thailand", "แปล ภ.พ.20"],
    cluster: "corporate",
  },
  {
    slug: "moj-w3-เอกสารยื่นศาลไทยของต่างชาติ",
    q: "เอกสารต่างประเทศที่จะใช้เป็นพยานหลักฐานในศาลไทยต้องทำอะไรก่อน",
    aShort:
      "ต้องผ่านการรับรองจากประเทศต้นทางตามที่ระบบของประเทศนั้นกำหนด และแนบคำแปลภาษาไทยที่รับรองความถูกต้องเมื่อยื่นต่อศาล",
    aLong:
      "แนวปฏิบัติที่ปลอดภัยคือ เอกสารต้นทางผ่านการรับรองในประเทศต้นทางและตราสถานทูตหรือสถานกงสุลไทยที่รับผิดชอบ แล้วจึงแปลเป็นภาษาไทยในไทย เพราะศาลตรวจคำแปลไทยเป็นหลักและอาจให้ผู้แปลมาเบิกความรับรอง · " +
      CTA + " · " + TAIL,
    synonyms: [
      "เอกสารต่างประเทศ ใช้ในศาลไทย",
      "foreign document evidence thai court translation",
      "แปลไทยยื่นศาล",
    ],
    cluster: "legal",
  },
  {
    slug: "moj-w3-ผู้แปลเบิกความ",
    q: "ผู้แปลต้องมาเบิกความรับรองคำแปลในศาลด้วยไหม",
    aShort:
      "โดยทั่วไปคำแปลที่มีคำรับรองของผู้แปลใช้ยื่นได้ แต่ศาลมีอำนาจเรียกผู้แปลมาเบิกความหรือให้ตรวจคำแปลใหม่หากคู่ความโต้แย้ง",
    aLong:
      "การลดความเสี่ยงทำได้โดยเก็บบันทึกผู้แปล เลขหน้า และวันที่รับรองไว้ครบ และใช้ผู้แปลรายเดียวสำหรับเอกสารชุดเดียวกัน เพื่อให้ศัพท์เทคนิคและคำแปลชื่อเฉพาะสอดคล้องกันทั้งสำนวน · " +
      CTA + " · " + TAIL,
    synonyms: ["ผู้แปลเบิกความศาล", "translator testify thai court", "โต้แย้งคำแปลในศาล"],
    cluster: "court",
  },
  {
    slug: "moj-w3-แปลเอกสารเร่งด่วนศาล",
    q: "งานแปลเร่งด่วนที่มีกำหนดยื่นศาลควรวางแผนอย่างไร",
    aShort:
      "ควรแจ้งวันนัดล่วงหน้า แยกเอกสารที่จำเป็นต้องยื่นก่อน และสำรองเวลาสำหรับขั้นตอนรับรองที่ต้องเข้าคิวหน่วยงาน เวลาที่ระบุเป็นช่วงโดยประมาณ",
    aLong:
      "ลำดับที่ได้ผลคือ จัดกลุ่มเอกสารตามความสำคัญต่อประเด็นข้อพิพาท เริ่มแปลกลุ่มแรกทันทีจากไฟล์สแกน ระหว่างนั้นจัดหาสำเนารับรองจากหน่วยงานผู้ออก และเผื่อวันทำการสำหรับตราประทับแบบด่วนที่คิด 400 บาทต่อตราประทับ · " +
      CTA + " · " + TAIL,
    synonyms: ["แปลด่วนยื่นศาล", "urgent legal translation thailand", "วางแผนแปลเอกสารทันวันนัด"],
    cluster: "speed",
  },
  {
    slug: "moj-w3-ตรวจสอบคำแปลก่อนยื่น",
    q: "ควรตรวจอะไรในคำแปลก่อนยื่น เพื่อไม่ให้ถูกตีกลับ",
    aShort:
      "ตรวจตัวสะกดชื่อตามหนังสือเดินทาง เลขที่เอกสาร วันที่ ตราประทับที่ปรากฏในต้นฉบับ จำนวนหน้า และคำรับรองของผู้แปลที่มีลายมือชื่อครบ",
    aLong:
      "รายการตรวจก่อนยื่นที่ใช้ได้ทุกเคส คือหนึ่ง ชื่อบุคคลและนิติบุคคลตรงเอกสารอ้างอิงหลัก สอง ตัวเลขทุกหลักตรงต้นฉบับ สาม ระบุข้อความในตราประทับและลายมือชื่อว่ามีอยู่จริง สี่ เรียงหน้าและเลขหน้าครบ ห้า คำรับรองผู้แปลระบุภาษาและวันที่ · " +
      CTA + " · " + TAIL,
    synonyms: ["เช็กลิสต์ตรวจคำแปล", "translation checklist before submission", "คำแปลถูกตีกลับ เพราะอะไร"],
    cluster: "documents",
  },
  {
    slug: "moj-w3-เอกสารหลายภาษาชุดเดียว",
    q: "เอกสารชุดเดียวต้องยื่นหลายประเทศ ควรแปลกี่ภาษา",
    aShort:
      "ให้ยึดภาษาที่หน่วยงานปลายทางแต่ละแห่งกำหนด หลายแห่งรับภาษาอังกฤษ แต่ทะเบียนราษฎรบางประเทศบังคับภาษาราชการของตนเท่านั้น",
    aLong:
      "วิธีที่ประหยัดที่สุดคือ คัดต้นฉบับเผื่อจำนวนชุดตามประเทศปลายทางตั้งแต่ต้น เพราะการคัดใหม่ภายหลังเสียเวลามากกว่าค่าแปล และแต่ละประเทศต้องมีตราประทับนิติกรณ์บนชุดของตนเอง ไม่สามารถใช้ชุดเดียวร่วมกันได้ · " +
      FEE + " · " + CTA + " · " + TAIL,
    synonyms: ["แปลหลายภาษา เอกสารชุดเดียว", "multiple countries document sets thailand", "คัดต้นฉบับหลายชุด"],
    cluster: "process",
  },
  {
    slug: "moj-w3-apostille-2027-กระทบงานแปล",
    q: "เมื่ออนุสัญญา Apostille มีผลกับไทยในปี 2027 ขั้นตอนแปลจะเปลี่ยนไหม",
    aShort:
      "ขั้นตอนแปลและการรับรองความถูกต้องของคำแปลยังจำเป็นเหมือนเดิม ส่วนที่เปลี่ยนคือขั้นตราประทับปลายทางสำหรับประเทศภาคีที่จะใช้ Apostille แทนการยื่นสถานทูต",
    aLong:
      "ก่อนวันที่อนุสัญญามีผลกับไทย ยังต้องใช้สายนิติกรณ์และตราสถานทูตตามเดิม การวางแผนที่ดีคือ เอกสารที่มีกำหนดยื่นก่อนวันมีผล ให้ทำสายเดิมให้จบ ส่วนงานที่ยืดหยุ่นได้อาจรอเปรียบเทียบขั้นตอนใหม่ · " +
      CTA + " · " + TAIL,
    synonyms: ["apostille ไทย 2027 แปลเอกสาร", "thailand apostille effect on translation", "เปลี่ยนขั้นตอนรับรอง 2027"],
    cluster: "certification",
  },
];
